import { Mic } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { useVoiceInput } from '@/features/assistant/hooks/useVoiceInput';
import { cn } from '@/utils/utils';

export function RecorderPage() {
  const { error: voiceError } = useVoiceInput();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleBootstrapPermission = useCallback(async () => {
    setIsProcessing(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Permission granted! Stop immediately.
      stream.getTracks().forEach((track) => track.stop());

      Office.context.ui.messageParent(JSON.stringify({ type: 'PERMISSION_GRANTED' }));
    } catch (error) {
      console.error('Permission denied in dialog:', error);
      toast.error('Microphone access denied. Please check your browser settings.');
      Office.context.ui.messageParent(
        JSON.stringify({ error: 'Permission denied', type: 'ERROR' }),
      );
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Auto-trigger on mount for best UX
  useEffect(() => {
    handleBootstrapPermission();
  }, [handleBootstrapPermission]);

  // We strictly only support Permission Bootstrapping here.
  // The sidebar handles all actual recording logic.
  return (
    <div className="bg-background h-viewport flex w-full flex-col items-center justify-center gap-4 p-6 text-center">
      <Toaster closeButton position="top-center" />
      <div className="border-highlight bg-info-bg rounded-full border p-4">
        <Mic className={cn('text-info h-8 w-8', isProcessing && 'animate-pulse')} />
      </div>

      <h2 className="text-xl font-bold">Microphone Access</h2>
      <p className="text-muted-foreground max-w-70 text-sm">
        {isProcessing
          ? 'Connecting to microphone...'
          : 'Lucio needs your permission to record audio for dictation.'}
      </p>

      {voiceError && (
        <p className="border-destructive-border bg-destructive-bg text-destructive rounded border p-2 text-xs">
          {voiceError === 'Permission denied'
            ? 'Access blocked. Please click the button below to allow.'
            : voiceError}
        </p>
      )}

      <Button
        className="bg-info hover:bg-info/90 mt-2 w-full max-w-50 rounded-full py-6 font-medium text-white"
        disabled={isProcessing}
        onClick={handleBootstrapPermission}
      >
        {isProcessing ? 'Waiting...' : 'Grant Access'}
      </Button>

      {!isProcessing && (
        <p className="text-muted-foreground mt-4 text-[10px] leading-relaxed opacity-50">
          This window will close automatically once access is granted.
        </p>
      )}
    </div>
  );
}
